import { memo, useEffect, useMemo } from 'react'
import { cn } from '@quickmd-llc/design-system'
import { useIntakeForms } from '../hooks/useIntakeForms'
import { useErrorToast } from '@/hooks/useErrorToast'
import { normalizeIntakeFormList } from '@/utils/narrowing'
import { IntakeFormsEmptyTable } from '../IntakeFormsEmptyTable/IntakeFormsEmptyTable'
import { IntakeFormsTableItem } from '../IntakeFormsTableItem/IntakeFormsTableItem'
import { IF_COL } from '../intakeFormsColumns'

/*
 * SCSS → Tailwind mapping (IntakeFormsTableBody.scss deleted):
 *   .if-table-wrapper: border border-shade-10 / rounded-b-large / overflow-visible / flex flex-col
 *   .if-table-header:  flex items-center gap-sp-2 / py-sp-1.5 px-sp-2 / bg-deep-navy-5 / border-b border-shade-10
 *   .if-header-label:  text-size-2 font-semibold leading-[18px] tracking-[0.14px] text-text-light whitespace-nowrap
 *   .if-table-body:    min-h-sp-10 (sp-10 = 80px)
 *
 * The list intentionally shows only the form Name + chevron (matching the legacy design). The
 * admin /intakeforms endpoint returns no per-form Service or Responses data, so those columns
 * (and the Service filter) were removed rather than render an empty pill / hardcoded "--".
 *
 * ErrorAlert → useErrorToast: fires toastError(error) whenever the error string changes
 * (no-op for falsy/empty values).
 */

const IntakeFormsTableBody = memo(() => {
  const { intakeFormsList: rawList, loading, error } = useIntakeForms()
  const intakeFormsList = useMemo(() => normalizeIntakeFormList(rawList), [rawList])

  const toastError = useErrorToast()

  useEffect(() => {
    toastError(error || null)
  }, [error, toastError])

  return (
    <div className='flex flex-col overflow-visible rounded-b-large border border-shade-10'>
      {/* Column header row */}
      <div className='flex items-center gap-sp-2 border-b border-shade-10 bg-deep-navy-5 px-sp-2 py-sp-1.5'>
        <div className={cn(IF_COL.base, IF_COL.icon)} />
        <div className={cn(IF_COL.base, IF_COL.name)}>
          <span className='whitespace-nowrap text-size-2 font-semibold leading-[18px] tracking-[0.14px] text-text-light'>
            Name
          </span>
        </div>
        <div className={cn(IF_COL.base, IF_COL.arrow)} />
      </div>

      {/* Rows */}
      <div className='min-h-sp-10'>
        {intakeFormsList.length > 0 ?
          intakeFormsList.map((item) => <IntakeFormsTableItem key={item.IntakeFormId} item={item} />)
        : loading ?
          null
        : <IntakeFormsEmptyTable />}
      </div>
    </div>
  )
})

IntakeFormsTableBody.displayName = 'IntakeFormsTableBody'

export { IntakeFormsTableBody }
